import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { Device } from '@ionic-native/device/ngx';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'src/modules/shared/services/local-storage.service';

export interface ErrorLogData {
  activityType: string;
  logLevel: 'error' | 'warn' | 'info' | 'debug' | 'trace';
  message: string;
  data?: any;
  page?: string;
  deviceType?: string;
  browserInfo?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorLoggingService {
  private isLoggingError = false; // Prevent infinite loops

  constructor(
    private http: HttpClient,
    private platform: Platform,
    private device: Device,
    private localStorageService: LocalStorageService
  ) {}

  /**
   * Log error to the backend API (used by global error handler)
   * @param error The error object
   * @param context Additional context about where the error occurred
   */
  async logError(error: any, context?: any): Promise<void> {
    return this.log('client_error', 'error', error, context);
  }

  /**
   * Manually log an error (use in catch blocks for handled exceptions)
   * @param message Error message
   * @param error Optional error object
   * @param context Additional context
   *
   * @example
   * try {
   *   await this.jobService.applyToJob(jobId);
   * } catch (error) {
   *   this.errorLoggingService.logHandledError('Failed to apply to job', error, { jobId });
   *   this.showErrorPopup(error);
   * }
   */
  async logHandledError(message: string, error?: any, context?: any): Promise<void> {
    const errorData = error || new Error(message);
    const enrichedContext = {
      ...context,
      handledError: true,
      originalMessage: message
    };
    return this.log('handled_error', 'error', errorData, enrichedContext);
  }

  /**
   * Log a warning (for non-critical issues)
   * @param message Warning message
   * @param data Additional data
   *
   * @example
   * this.errorLoggingService.logWarning('User token expired, refreshing...', { userId });
   */
  async logWarning(message: string, data?: any): Promise<void> {
    return this.log('warning', 'warn', new Error(message), data);
  }

  /**
   * Log info/activity (for tracking user actions)
   * @param activityType Type of activity
   * @param message Activity message
   * @param data Additional data
   *
   * @example
   * this.errorLoggingService.logActivity('job_application', 'User applied to job', { jobId, jobTitle });
   */
  async logActivity(activityType: string, message: string, data?: any): Promise<void> {
    return this.log(activityType, 'info', { message }, data);
  }

  /**
   * Core logging method
   * @private
   */
  private async log(
    activityType: string,
    logLevel: 'error' | 'warn' | 'info' | 'debug' | 'trace',
    error: any,
    context?: any
  ): Promise<void> {
    // Prevent infinite loops if logging itself fails
    if (this.isLoggingError) {
      return;
    }

    try {
      this.isLoggingError = true;

      // Extract error information
      const errorMessage = this.extractErrorMessage(error);
      const stackTrace = this.extractStackTrace(error);
      const page = this.getCurrentPage();

      // Prepare error data
      const errorData: ErrorLogData = {
        activityType,
        logLevel,
        message: errorMessage,
        data: {
          stack: stackTrace,
          errorType: error?.constructor?.name || 'Error',
          context: context || {},
          timestamp: new Date().toISOString(),
          url: window.location.href
        },
        page: page,
        deviceType: this.getDeviceType(),
        browserInfo: this.getBrowserInfo()
      };

      // Send to backend (fire and forget - don't await)
      this.sendErrorLog(errorData).catch(err => {
        // Silently fail if logging fails
        console.error('Failed to log error to backend:', err);
      });
    } catch (err) {
      // If error logging fails, just log to console
      console.error('Error in error logging service:', err);
    } finally {
      this.isLoggingError = false;
    }
  }

  /**
   * Send error log to backend API
   */
  private async sendErrorLog(errorData: ErrorLogData): Promise<void> {
    // Get auth token from local storage
    const token = await this.localStorageService.getLoggedInUserToken();

    // If no token, user is not authenticated - skip logging to backend
    if (!token) {
      return;
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    const url = `${environment.API_BASE_URL}/api/client-activity-logs`;

    // Use timeout to ensure we don't block for too long
    await Promise.race([
      this.http.post(url, errorData, { headers }).toPromise(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 5000)
      )
    ]);
  }

  /**
   * Extract error message from error object
   */
  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error?.originalError?.message) {
      return error.originalError.message;
    }

    if (error?.message) {
      return error.message;
    }

    if (error?.error?.message) {
      return error.error.message;
    }

    if (typeof error?.error === 'string') {
      return error.error;
    }

    return 'Unknown error occurred';
  }

  /**
   * Extract stack trace from error object
   */
  private extractStackTrace(error: any): string {
    if (error?.originalError?.stack) {
      return error.originalError.stack;
    }

    if (error?.stack) {
      return error.stack;
    }

    return '';
  }

  /**
   * Get current page/route
   */
  private getCurrentPage(): string {
    try {
      return window.location.pathname || '';
    } catch {
      return '';
    }
  }

  /**
   * Get device type information
   */
  private getDeviceType(): string {
    try {
      if (this.platform.is('cordova') || this.platform.is('capacitor')) {
        // Native app
        const platform = this.device.platform || 'unknown';
        const model = this.device.model || '';
        return `${platform} - ${model}`;
      } else if (this.platform.is('mobile')) {
        return 'mobile-web';
      } else if (this.platform.is('tablet')) {
        return 'tablet-web';
      } else if (this.platform.is('desktop')) {
        return 'desktop-web';
      }
      return 'unknown';
    } catch {
      return 'unknown';
    }
  }

  /**
   * Get browser information
   */
  private getBrowserInfo(): string {
    try {
      const userAgent = navigator.userAgent;

      // Extract browser name and version
      let browserName = 'Unknown';
      let browserVersion = 'Unknown';

      if (userAgent.indexOf('Chrome') > -1) {
        browserName = 'Chrome';
        const match = userAgent.match(/Chrome\/(\d+)/);
        if (match) browserVersion = match[1];
      } else if (userAgent.indexOf('Safari') > -1) {
        browserName = 'Safari';
        const match = userAgent.match(/Version\/(\d+)/);
        if (match) browserVersion = match[1];
      } else if (userAgent.indexOf('Firefox') > -1) {
        browserName = 'Firefox';
        const match = userAgent.match(/Firefox\/(\d+)/);
        if (match) browserVersion = match[1];
      } else if (userAgent.indexOf('Edge') > -1) {
        browserName = 'Edge';
        const match = userAgent.match(/Edge\/(\d+)/);
        if (match) browserVersion = match[1];
      }

      return `${browserName} ${browserVersion}`;
    } catch {
      return 'Unknown';
    }
  }
}
